// @ts-nocheck
import { PropsWithChildren, ReactElement, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery } from 'react-query';
import qs from 'query-string';
import { Column } from 'react-table';
import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';
import { IResponseData } from 'types/api';
import ReactTableWrapper from 'components/table/ReactTableWrapper';
import { ISelectOptionProps } from 'components/form/types';
import { ITableFilter, IFilterOptions, ISelectActionButton } from 'components/table/types';
import { formatDate } from 'utils/helpers/formatters';
import { getUserInfo } from 'utils/auth';

export type SetSortParamType = (sortParam: string | null) => void;

interface IProps<T extends Record<string, unknown>> {
  entityName: string;
  entityEndpoint: string;
  searchParams?: any;
  columns: Array<Column<T>>;
  loading?: boolean;
  requestType?: 'GET' | 'POST';
  filters?: {
    [key: string]: any;
    name: keyof IFilterOptions;
    placeholder?: string;
    options?: ISelectOptionProps[];
    size?: string;
  }[];
  onFiltersChange?: (filters: IFilterOptions) => void;
  hasPermission?: string[];
  hasImportPermission?: boolean;
  onImport?: () => void;
  onAdd?: () => void;
  onMultipleActivation?: () => void;
  addBtnLabel?: string;
  addBtnTestId?: string;
  multipleActivateBtnLabel?: string;
  pagination?: boolean;
  pageLimit?: number;
  hideFilterBar?: boolean;
  hideSearch?: boolean;
  className?: string;
  hasTitle?: boolean;
  updateTitle?: (data: any) => JSX.Element;
  isExpandable?: boolean;
  isSelectable?: boolean;
  customSelectableRowProps?: string[];
  useCustomSelectBtn?: boolean;
  toggleSelectBtnLabel?: string;
  selectActionButtons?: ISelectActionButton[];
  skipResetExpanded?: boolean;
  isReport?: boolean;
  showDownloadButton?: () => void;
  expandAllRows?: boolean;
  sorDatetDescQuery?: boolean;
  setExpandAllRows?: (val: boolean) => void;
  onDataFetched?: (data: IResponseData<T>) => void;
  disablePagination?: boolean;
  reloadKey?: boolean;
  isLeavesMobile?: boolean;
  setQueryString?: (queryString: string) => void;
  showOnlyWaiting?: boolean;
  showOnlyUnreviewd?: boolean;
  showLeaveReview?: boolean;
  disableMultiSort?: boolean;
  useBackendSorting?: boolean;
  showID?: boolean;
  showFilterWarning?: boolean;
  unprocessedLeaves?: {
    count: number;
    min_date: string | null;
    max_date: string | null;
  } | null;
}

function FetchTable<T extends Record<string, unknown>>({
  filters: filterOptions,
  onFiltersChange,
  entityName,
  entityEndpoint,
  columns,
  loading,
  requestType = 'GET',
  hasPermission,
  hasImportPermission,
  onImport,
  onAdd,
  onMultipleActivation,
  addBtnLabel,
  addBtnTestId,
  multipleActivateBtnLabel,
  pagination,
  pageLimit,
  hideFilterBar,
  hideSearch,
  className,
  hasTitle,
  updateTitle,
  isExpandable,
  isSelectable,
  customSelectableRowProps,
  useCustomSelectBtn,
  toggleSelectBtnLabel,
  selectActionButtons,
  skipResetExpanded,
  isReport,
  showDownloadButton,
  expandAllRows,
  setExpandAllRows,
  searchParams = {},
  sorDatetDescQuery,
  onDataFetched,
  disablePagination,
  reloadKey,
  isLeavesMobile,
  setQueryString,
  showOnlyWaiting,
  showOnlyUnreviewd,
  showLeaveReview,
  disableMultiSort,
  useBackendSorting,
  showID,
  showFilterWarning,
  unprocessedLeaves,
}: PropsWithChildren<IProps<T>>): ReactElement {
  const buildQueryFilters = (filterOpt: IFilterOptions[]) => {
    const FilterOptArr = filterOpt?.reduce((acc, curr) => {
      if (curr.name === 'leave_period')
        return {
          ...acc,
          month: new Date().getMonth() + 1,
          year: new Date().getFullYear(),
        };
      if (curr.name === 'leave_period_start') {
        const date = new Date();
        date.setMonth(date.getMonth(), 1);
        return {
          ...acc,
          date_from: formatDate(date, 'YYY-mm-dd'),
        };
      }
      if (curr.name === 'leave_period_end') {
        const date = new Date();
        // Set endDate to the last day of the same month
        date.setMonth(date.getMonth() + 1, 0);

        if (
          (['leavesCompleted', 'leavesWaitingApproval'].includes(curr.subType) && curr.setLastMonth) ||
          curr.subType === 'leaveNextMonth'
        ) {
          if (date.getMonth() === 11) {
            date.setFullYear(date.getFullYear() + 1);
            date.setMonth(0);
            date.setDate(31);
          } else {
            date.setMonth(11);
            date.setDate(31);
          }
        }

        if (curr.setLastMonth) {
          date.setMonth(11);
          date.setDate(31);
        }

        return {
          ...acc,
          date_to: formatDate(date, 'YYY-mm-dd'),
        };
      }
      if (curr.options?.length) {
        let selected = null;
        if (curr.isMulti) {
          selected = curr.options?.filter(opt => opt.selected).map(opt => opt.value.toString());
          return {
            ...acc,
            [curr.name]: selected?.length ? selected : null,
          };
        }
        selected = curr.options?.find(opt => opt.selected)?.value;
        return {
          ...acc,
          [curr.name]: selected ? selected.toString() : null,
        };
      }
      return { ...acc, [curr.name]: null };
    }, {});
    return FilterOptArr;
  };

  const [title, setTitle] = useState<JSX.Element | null>(null);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(pageLimit || (pagination ? 20 : 50));
  const [search, setSearch] = useState<string | null>(null);
  const [filters, setFilters] = useState<IFilterOptions | undefined>(() =>
    filterOptions ? buildQueryFilters(filterOptions) : undefined
  );
  const [sortParam, setSortParam] = useState<string | null>(null);
  const [checkWithTooltip, setCheckWithTooltip] = useState<number[]>([]);
  const sortParamRef = useRef<string | null>(null);
  const isInitialMount = useRef(true);

  const setSortParamRef: SetSortParamType = useCallback(sp => {
    sortParamRef.current = sp;
  }, []);

  const { featureLeaveReview } = getUserInfo();

  const onDataFetchedRef = useRef<((data: IResponseData<T>) => void) | undefined>();
  onDataFetchedRef.current = onDataFetched;

  useEffect(() => {
    if (!filters) setFilters(buildQueryFilters(filterOptions));
  }, [filterOptions, filters]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setFilters(buildQueryFilters(filterOptions));
  }, [filterOptions]);

  useEffect(() => {
    if (filters && onFiltersChange) {
      onFiltersChange(filters);
    }
  }, [filters, onFiltersChange]);

  useEffect(() => {
    /**
     * If the component is being used in Report page, on report filters change
     * we should reset page offset, so that it always shows first results first
     */
    if (isReport) {
      setOffset(0);
    }
  }, [searchParams]);

  const fetchEntity = useCallback((): Promise<IResponseData<T>> => {
    let queryString = qs.stringify(
      {
        limit,
        offset,
        search,
        ...filters,
        ...searchParams,
      },
      { arrayFormat: 'bracket', skipNull: true }
    );

    if (featureLeaveReview && showLeaveReview) {
      queryString = `${queryString}&reviewed=${showOnlyUnreviewd}`;
    }

    if (sorDatetDescQuery) {
      queryString = `${queryString}&sort[0][name]=date_from&sort[0][order]=desc`;
    }

    if (!sorDatetDescQuery && sortParamRef.current && useBackendSorting) {
      queryString = `${queryString}&${sortParamRef.current ?? ''}`;
    }

    if (setQueryString) {
      setQueryString(queryString);
    }

    const requestData = {
      limit,
      offset,
      search,
      ...filters,
      ...searchParams,
    };

    if (requestType === 'POST') {
      return API.post<IResponseData<T>>(entityEndpoint, requestData).then(res => {
        if (showOnlyWaiting) {
          res.data.items = res.data.items.filter(item => {
            return item.badge && item.badge.type === 'waiting_action';
          });
        }

        if (showOnlyUnreviewd) {
          res.data.items = res.data.items.filter(item => item.reviewed_by && item.reviewed_at);
        }

        if (hasTitle && !!updateTitle) setTitle(updateTitle(res.data));

        if (isReport) {
          if (setExpandAllRows) setExpandAllRows(false);
          if (showDownloadButton) showDownloadButton();
        }
        if (onDataFetchedRef.current) {
          onDataFetchedRef.current(res.data);
        }
        return res.data;
      });
    }

    return API.get<IResponseData<T>>(`${entityEndpoint}?${queryString}`).then(res => {
      if (showOnlyWaiting) {
        res.data.items = res.data.items.filter(item => {
          return item.badge && item.badge.type === 'waiting_action';
        });
      }

      if (showOnlyUnreviewd) {
        res.data.items = res.data.items.filter(item => item.reviewed_by && item.reviewed_at);
      }

      if (hasTitle && !!updateTitle) setTitle(updateTitle(res.data));

      if (isReport) {
        if (setExpandAllRows) setExpandAllRows(false);
        if (showDownloadButton) showDownloadButton();
      }
      if (onDataFetchedRef.current) {
        onDataFetchedRef.current(res.data);
      }
      return res.data;
    });
  }, [
    entityEndpoint,
    featureLeaveReview,
    filters,
    hasTitle,
    isReport,
    limit,
    offset,
    requestType,
    search,
    searchParams,
    setExpandAllRows,
    setQueryString,
    showDownloadButton,
    showLeaveReview,
    showOnlyUnreviewd,
    showOnlyWaiting,
    sorDatetDescQuery,
    sortParam,
    updateTitle,
  ]);

  const { isFetching, isLoading, error, data } = useQuery<IResponseData<T>, Error>(
    [entityName, offset, limit, search, filters, searchParams, reloadKey],
    (): Promise<IResponseData<T>> => {
      return fetchEntity();
    }
  );

  const updateFilter = useCallback(
    (
      name: keyof IFilterOptions,
      selected: ISelectOptionProps | ISelectOptionProps[] | Date,
      isMulti?: boolean,
      type?: 'datepicker'
    ) => {
      setOffset(0);
      if (!filters) {
        return;
      }
      if (selected) {
        if (!isMulti) {
          // Filter type `datepicker`
          if (type === 'datepicker' || type === 'alldatepicker') {
            const date = selected as Date;
            return setFilters({
              ...filters,
              [name]: [formatDate(date, 'YYY/mm/dd')],
            });
          }
          // Filter type `monthyearpicker`
          if (type === 'monthyearpicker') {
            const date = selected as Date;
            return setFilters({
              ...filters,
              month: (date.getMonth() + 1).toString(),
              year: date.getFullYear().toString(),
            });
          }
          // Filter type `yearpicker`
          if (type === 'yearpicker') {
            const date = selected as Date;
            return setFilters({
              ...filters,
              year: date.getFullYear().toString(),
            });
          }
          // Filter type `monthyearpickerstart`
          if (type === 'monthyearpickerstart') {
            const dateFrom = selected as Date;
            // In the cases when dateFrom is the same month and year as dateTo, we set the
            // dateFrom value to be equal to the start of the current month and dateTo
            // value to be equal to the last day of the month, so that we can fetch the
            // entities for the current selected month period
            const dateTo = new Date(filters.date_to);
            const isSameMonthAndYear =
              dateFrom.getMonth() === dateTo.getMonth() && dateFrom.getYear() === dateTo.getYear();
            if (isSameMonthAndYear) {
              let nextMonth = dateTo.setMonth(dateFrom.getMonth() + 1, 0);
              nextMonth = new Date(nextMonth);
              return setFilters({
                ...filters,
                date_from: formatDate(dateFrom, 'YYY-mm-dd'),
                date_to: formatDate(nextMonth, 'YYY-mm-dd'),
              });
            }

            return setFilters({
              ...filters,
              date_from: formatDate(dateFrom, 'YYY-mm-dd'),
            });
          }
          // Filter type `monthyearpickerend`
          if (type === 'monthyearpickerend') {
            const date = selected as Date;
            // For the endDate we set the last day of the month
            let setLastDayOfTheMonth = date.setMonth(date.getMonth() + 1, 0);
            setLastDayOfTheMonth = new Date(setLastDayOfTheMonth);

            return setFilters({
              ...filters,
              date_to: formatDate(setLastDayOfTheMonth, 'YYY-mm-dd'),
            });
          }

          const option = selected as ISelectOptionProps;
          if (option.value === 'datepicker' || option.value === 'alldatepicker')
            return setFilters({
              ...filters,
              [name]: ['date-dd.MM.yyyy H:mm', 'date-dd.MM.yyyy', 'date-dd.MM', 'date-yyyy'],
            });
          return setFilters({
            ...filters,
            [name]: option.value.toString(),
          });
        }

        const options = selected as ISelectOptionProps[];
        return setFilters({
          ...filters,
          [name]: options.map(opt => opt.value.toString()),
        });
      }

      return setFilters({
        ...filters,
        [name]: null,
      });
    },
    [filters]
  );

  const setCustomSelectableRowIds = useMemo(() => {
    if (!customSelectableRowProps?.length) return [];

    // For download mode, enable all rows
    if (customSelectableRowProps.includes('CAN_MULTI_DOWNLOAD')) {
      return data?.items.map(row => row.id) || [];
    }

    const tooltipIds: number[] = [];
    const selectableRowIds = data?.items
      .filter(row => {
        const hasCustomProperty = customSelectableRowProps.some(curr => !!row[curr]);

        if (hasCustomProperty) {
          if (row.has_enough_leave_days === false) {
            tooltipIds.push(row.id);
            return false;
          }
          return true;
        }

        return false;
      })
      .map(row => row.id);

    setCheckWithTooltip(tooltipIds);
    return selectableRowIds;
  }, [data, customSelectableRowProps]);

  const tableFilters: ITableFilter[] | undefined = useMemo(
    () =>
      filterOptions
        ?.filter(filter => !filter.hidden)
        .map(filter => {
          const filterValue = filters?.[filter.name];
          if (filter.name === 'group') {
            console.log('Group filter - value from filters state:', filterValue);
            console.log(
              'Group filter - options with selected:',
              filter.options?.map(o => ({ value: o.value, selected: o.selected }))
            );
          }
          return {
            name: filter.name,
            value: filterValue,
            placeholder: filter.placeholder,
            options: filter.options,
            isMulti: filter.isMulti,
            showTotal: filter.showTotal,
            type: filter.type,
            subType: filter.subType,
            limitOptions: filter.limitOptions,
            allowSelectAll: filter.allowSelectAll,
            icon: filter.icon,
            ariaLabel: filter.ariaLabel,
            onChange: (selected: ISelectOptionProps | ISelectOptionProps[] | Date) => {
              updateFilter(filter.name, selected, filter.isMulti, filter.type);
            },
            size: filter.size,
          };
        }),
    [filterOptions, filters, updateFilter]
  );

  return (
    <>
      {hasTitle && <h3 className="h5 mb-4">{title}</h3>}
      <ReactTableWrapper<T>
        loading={loading || isFetching || isLoading}
        error={error && createErrorFromResponse(error).message}
        data={data?.items || []}
        columns={columns}
        hasPermission={hasPermission}
        hasImportPermission={hasImportPermission}
        onImport={onImport}
        onAdd={onAdd}
        onMultipleActivation={onMultipleActivation}
        addBtnLabel={addBtnLabel}
        addBtnTestId={addBtnTestId}
        isLeavesMobile={isLeavesMobile}
        multipleActivateBtnLabel={multipleActivateBtnLabel}
        pagination={pagination}
        offset={offset}
        limit={limit}
        totalCount={data?.totalCount || 0}
        setOffset={setOffset}
        setLimit={setLimit}
        search={search}
        setSearch={value => {
          setSearch(value);
          setOffset(0);
        }}
        filters={tableFilters}
        hideFilterBar={hideFilterBar}
        className={className}
        entityName={entityName}
        isExpandable={isExpandable}
        isSelectable={isSelectable}
        customSelectableRowIds={isSelectable ? setCustomSelectableRowIds : undefined}
        useCustomSelectBtn={useCustomSelectBtn}
        toggleSelectBtnLabel={toggleSelectBtnLabel}
        selectActionButtons={selectActionButtons}
        skipResetExpanded={skipResetExpanded}
        docTab={searchParams.tab}
        expandAllRows={expandAllRows}
        hideSearch={hideSearch}
        disablePagination={disablePagination}
        setSortParam={setSortParam}
        setSortParamRef={setSortParamRef}
        disableMultiSort={disableMultiSort}
        useBackendSorting={useBackendSorting}
        checkWithTooltip={checkWithTooltip}
        showID={showID}
        showFilterWarning={showFilterWarning}
        unprocessedLeaves={unprocessedLeaves}
      />
    </>
  );
}

export default FetchTable;
